
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Plus } from 'lucide-react';
import { VideoCarousel } from './youtube-videos/VideoCarousel';
import { VideoSortControls } from './youtube-videos/VideoSortControls';
import { AddVideoDialog } from './AddVideoDialog';
import { EmptyVideosList } from './youtube-videos/EmptyVideosList';
import { YouTubeVideo } from '@/services/youtubeVideoService';

export type VideoSortOption = 'newest' | 'oldest' | 'alphabetical';

interface YouTubeVideosListProps {
  techniqueId: string;
  onVideoAdded: () => Promise<void>;
  onVideoDeleted: () => Promise<void>;
  sortBy: VideoSortOption;
  onSortChange: (option: VideoSortOption) => void;
  className?: string;
  videosList?: YouTubeVideo[];
}

export const YouTubeVideosList: React.FC<YouTubeVideosListProps> = ({
  techniqueId,
  onVideoAdded,
  onVideoDeleted,
  sortBy,
  onSortChange,
  className = '',
  videosList = []
}) => {
  const [isAddDialogOpen, setIsAddDialogOpen] = React.useState(false);

  const handleVideoAdded = async () => {
    await onVideoAdded();
    setIsAddDialogOpen(false);
  };

  if (videosList.length === 0) {
    return (
      <>
        <Card className={className}>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>Additional Videos</CardTitle>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => setIsAddDialogOpen(true)}
              className="gap-2"
            >
              <Plus className="h-4 w-4" />
              Add Video
            </Button>
          </CardHeader>
          <CardContent>
            <EmptyVideosList />
          </CardContent>
        </Card>

        <AddVideoDialog
          open={isAddDialogOpen}
          onOpenChange={setIsAddDialogOpen}
          techniqueId={techniqueId}
          onVideoAdded={handleVideoAdded}
        />
      </>
    );
  }

  return (
    <>
      <Card className={className}>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Additional Videos ({videosList.length})</CardTitle>
          <div className="flex items-center gap-2">
            <VideoSortControls sortBy={sortBy} onSortChange={onSortChange} />
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => setIsAddDialogOpen(true)}
              className="gap-2"
            >
              <Plus className="h-4 w-4" />
              Add Video
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <VideoCarousel 
            videos={videosList} 
            onVideoDeleted={onVideoDeleted}
          />
        </CardContent>
      </Card>

      <AddVideoDialog
        open={isAddDialogOpen}
        onOpenChange={setIsAddDialogOpen}
        techniqueId={techniqueId}
        onVideoAdded={handleVideoAdded}
      />
    </>
  );
};
